export type MemberReportCount = {
  label: string;
  count: number;
};

export type MemberReportRegistration = {
  id: string;
  district: string;
  category: string;
  products: number;
  joined: string;
};

export type MemberReportData = {
  period: string;
  periodMembers: number;
  allTimeMembers: number;
  profilesWithProducts: number;
  profilesWithMobile: number;
  profilesWithLocation: number;
  districts: MemberReportCount[];
  categories: MemberReportCount[];
  recentRegistrations: MemberReportRegistration[];
};

function pdfText(value: string) {
  return value.normalize("NFKD").replace(/[^\x20-\x7E]/g, "").trim();
}

export async function generateMemberSummaryPdf(data: MemberReportData) {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const contentWidth = pageWidth - margin * 2;
  const navy: [number, number, number] = [7, 53, 128];
  const blue: [number, number, number] = [17, 135, 210];
  const green: [number, number, number] = [11, 138, 107];
  const muted: [number, number, number] = [82, 109, 147];
  const pale: [number, number, number] = [244, 248, 252];
  const line: [number, number, number] = [219, 228, 238];
  const generatedAt = new Date();
  const generatedLabel = new Intl.DateTimeFormat("en-MY", {
    timeZone: "Asia/Kuching",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZoneName: "short",
  }).format(generatedAt);

  const truncate = (value: string, maxWidth: number) => {
    const clean = pdfText(value) || "Not specified";
    if (doc.getTextWidth(clean) <= maxWidth) return clean;
    let shortened = clean;
    while (shortened.length > 1 && doc.getTextWidth(`${shortened}...`) > maxWidth) shortened = shortened.slice(0, -1);
    return `${shortened}...`;
  };

  const drawBreakdown = (title: string, rows: MemberReportCount[], x: number, y: number, width: number, color: [number, number, number]) => {
    doc.setTextColor(...navy);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(title, x, y);
    const visibleRows = rows.slice(0, 7);
    const maxCount = Math.max(1, ...visibleRows.map((row) => row.count));
    visibleRows.forEach((row, index) => {
      const rowY = y + 7 + index * 5.4;
      const labelWidth = width * 0.42;
      const trackX = x + labelWidth + 2;
      const trackWidth = width - labelWidth - 14;
      doc.setTextColor(...muted);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.1);
      doc.text(truncate(row.label, labelWidth), x, rowY + 1.1);
      doc.setFillColor(...pale);
      doc.roundedRect(trackX, rowY - 1.5, trackWidth, 2.8, 1.4, 1.4, "F");
      doc.setFillColor(...color);
      doc.roundedRect(trackX, rowY - 1.5, Math.max(1.2, trackWidth * (row.count / maxCount)), 2.8, 1.4, 1.4, "F");
      doc.setTextColor(...navy);
      doc.setFont("helvetica", "bold");
      doc.text(row.count.toLocaleString("en-MY"), x + width, rowY + 1.1, { align: "right" });
    });
    if (visibleRows.length === 0) {
      doc.setTextColor(...muted);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text("No records available.", x, y + 8);
    }
  };

  doc.setFillColor(...navy);
  doc.rect(0, 0, pageWidth, 30, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Kitanego Members Summary", margin, 13);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.2);
  doc.text(`Reporting period: ${pdfText(data.period)}`, margin, 20);
  doc.text(`Generated: ${pdfText(generatedLabel)}`, margin, 25);

  const metrics = [
    ["Period members", data.periodMembers],
    ["All-time members", data.allTimeMembers],
    ["With products", data.profilesWithProducts],
    ["With mobile", data.profilesWithMobile],
    ["With location", data.profilesWithLocation],
    ["Districts", data.districts.length],
  ] as const;
  const metricGap = 3;
  const metricWidth = (contentWidth - metricGap * 2) / 3;
  metrics.forEach(([label, value], index) => {
    const row = Math.floor(index / 3);
    const column = index % 3;
    const x = margin + column * (metricWidth + metricGap);
    const y = 36 + row * 18;
    doc.setFillColor(...pale);
    doc.roundedRect(x, y, metricWidth, 15, 2.5, 2.5, "F");
    doc.setTextColor(...muted);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.8);
    doc.text(label, x + 3, y + 5.5);
    doc.setTextColor(...navy);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11.5);
    doc.text(value.toLocaleString("en-MY"), x + 3, y + 12);
  });

  const columnGap = 10;
  const columnWidth = (contentWidth - columnGap) / 2;
  drawBreakdown("Members by district", data.districts, margin, 81, columnWidth, blue);
  drawBreakdown("Strongest categories", data.categories, margin + columnWidth + columnGap, 81, columnWidth, green);

  const tableY = 132;
  doc.setTextColor(...navy);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Latest registrations", margin, tableY);
  doc.setFillColor(...navy);
  doc.roundedRect(margin, tableY + 5, contentWidth, 8, 2, 2, "F");
  const columns = [margin + 3, margin + 34, margin + 75, margin + 126, margin + 153];
  const headers = ["Member ID", "District", "Strongest signal", "Products", "Joined"];
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(6.6);
  headers.forEach((header, index) => doc.text(header, columns[index], tableY + 10.1));

  data.recentRegistrations.slice(0, 10).forEach((row, index) => {
    const y = tableY + 13 + index * 8;
    if (index % 2 === 0) {
      doc.setFillColor(...pale);
      doc.rect(margin, y, contentWidth, 8, "F");
    }
    doc.setTextColor(...muted);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.8);
    doc.text(truncate(row.id, 28), columns[0], y + 5.1);
    doc.text(truncate(row.district, 37), columns[1], y + 5.1);
    doc.text(truncate(row.category, 46), columns[2], y + 5.1);
    doc.text(row.products.toLocaleString("en-MY"), columns[3], y + 5.1);
    doc.text(truncate(row.joined, 27), columns[4], y + 5.1);
  });
  if (data.recentRegistrations.length === 0) {
    doc.setTextColor(...muted);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("No member registrations are available for this reporting period.", margin + 3, tableY + 21);
  }

  doc.setDrawColor(...line);
  doc.line(margin, pageHeight - 16, pageWidth - margin, pageHeight - 16);
  doc.setTextColor(...muted);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.text("Administrator summary report. Direct contact details are excluded.", margin, pageHeight - 10);
  doc.text("A4 portrait", pageWidth - margin, pageHeight - 10, { align: "right" });

  const fileTimestamp = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kuching",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(generatedAt).replace(/[^0-9]/g, "").slice(0, 12);
  doc.save(`kitanego-members-summary-${fileTimestamp}.pdf`);
}
